import quoteServices from '../../services/oneButtonServices/quoteServices';
import { useOneButtonStore } from '../../store/oneButtonStore';
import {
    getContractApiErrorMessage,
    getContractApiResultErrorMessage,
} from '../../helpers/contractApiErrorMessage';
import {
    ensureLeadAndContactForProduct,
    financePartnerProductCode,
    financePartnerProductTypeLabel,
    isFinancePartnerSyncFlagDone,
    loadFinancePartnerSyncStatuses,
    partnerNeedsUtilityAndLeadSystem,
    syncLeadSystemWithRetries,
    syncUtilityWithRetries,
    type FinancePartnerSyncStatusRow,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/utils/finance';
import { partnerRequiresQuoteSyncChecklist } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/partnerSyncConfig';
import { partnerKeysEqual } from './preContractProductPartners';
import type { PreContractProduct } from './preContractTypes';

export const QUOTE_FORM_DISMISSED = 'QUOTE_FORM_DISMISSED';
export const GENERATE_STEP_DISMISSED = 'GENERATE_STEP_DISMISSED';

export function isQuoteFormDismissed(error: unknown): boolean {
    return error instanceof Error && error.message === QUOTE_FORM_DISMISSED;
}

export function isGenerateStepDismissed(error: unknown): boolean {
    return (
        isQuoteFormDismissed(error) ||
        (error instanceof Error &&
            (error.message === GENERATE_STEP_DISMISSED || error.message === 'FINALIZE_CANCELLED'))
    );
}

export type ProductGenerateRunResult = {
    ok: boolean;
    error?: string;
    dismissed?: boolean;
};

export type ProductGenerateStepId =
    | 'finalize'
    | 'utilityDetails'
    | 'partnerSync'
    | 'lead'
    | 'utility'
    | 'leadSystem'
    | 'quote'
    | 'contract';

export type ProductGenerateStepStatus =
    | 'pending'
    | 'running'
    | 'synced'
    | 'done'
    | 'skipped'
    | 'error';

export type ProductGenerateStep = {
    id: ProductGenerateStepId;
    label: string;
    status: ProductGenerateStepStatus;
    detail?: string;
    /** V2 Partner Sync / design gate — step is visible but not runnable. */
    blockedReason?: string;
    /** Saved finance quote id for the Create quote step. */
    quoteId?: string;
};

export type RunProductGeneratePipelineArgs = {
    product: PreContractProduct;
    opportunityId: string;
    partnerName: string;
    /** A V2 lead-selection form already confirmed the product-scoped lead. */
    leadAlreadyConfirmed?: boolean;
    onStepUpdate: (step: ProductGenerateStep) => void;
    waitForQuoteSaved: () => Promise<string | undefined>;
};

/**
 * Cash, GoodLeap, Enfin, and Service Finance go straight to the quote form in V2 — CreateLead and the
 * utility / lead-system syncs only exist for the checklist partners (IGS, LightReach).
 */
export function partnerSkipsQuotePreSync(partnerName: string): boolean {
    if (!partnerName.trim()) return false;
    return !partnerRequiresQuoteSyncChecklist(partnerName);
}

export function withPartnerSyncStep(
    steps: ProductGenerateStep[],
    include: boolean,
): ProductGenerateStep[] {
    if (!include) return steps;
    return [{ id: 'partnerSync', label: 'Sync partner', status: 'pending' }, ...steps];
}

export type OpportunityGenerateStepFlags = {
    needsFinalize?: boolean;
    needsUtilityDetails?: boolean;
    needsPartnerSync?: boolean;
};

/** Finalize (solar/battery) → utility details → finance partner → quote / contract. */
export function buildOpportunityGenerateSteps(
    product: PreContractProduct,
    partnerName: string,
    flags: OpportunityGenerateStepFlags,
): ProductGenerateStep[] {
    let steps = buildInitialGenerateSteps(product, partnerName);
    const partnerLabel = partnerName.trim();
    steps = [
        {
            id: 'partnerSync',
            label: 'Select finance partner',
            status: flags.needsPartnerSync ? 'pending' : partnerLabel ? 'synced' : 'pending',
            detail: flags.needsPartnerSync
                ? 'Choose and save the finance partner'
                : partnerLabel
                  ? `${partnerLabel} saved`
                  : 'Choose a finance partner',
        },
        ...steps,
    ];
    if (flags.needsUtilityDetails || product === 'Solar' || product === 'Battery') {
        steps = [
            {
                id: 'utilityDetails',
                label: 'Update utility details',
                status: flags.needsUtilityDetails ? 'pending' : 'synced',
                detail: flags.needsUtilityDetails
                    ? 'Save the changed utility company, usage, rate, or escalator'
                    : 'Utility details saved',
            },
            ...steps,
        ];
    }
    if (product === 'Solar' || product === 'Battery' || flags.needsFinalize) {
        steps = [
            {
                id: 'finalize',
                label: 'Finalize design',
                status: flags.needsFinalize ? 'pending' : 'synced',
                detail: flags.needsFinalize
                    ? 'Confirm the selected oneDRAW design'
                    : 'Design finalized',
            },
            ...steps,
        ];
    }
    return steps;
}

export function buildInitialGenerateSteps(
    product: PreContractProduct,
    partnerName: string,
): ProductGenerateStep[] {
    const steps: ProductGenerateStep[] = [];

    if (!partnerSkipsQuotePreSync(partnerName)) {
        steps.push({ id: 'lead', label: 'Create lead', status: 'pending' });
    }

    if (partnerNeedsUtilityAndLeadSystem(partnerName)) {
        steps.push(
            { id: 'utility', label: 'Sync utility', status: 'pending' },
            { id: 'leadSystem', label: 'Sync lead system', status: 'pending' },
        );
    }

    steps.push(
        { id: 'quote', label: 'Create quote', status: 'pending' },
        { id: 'contract', label: 'Generate contract', status: 'pending' },
    );

    return steps;
}

export function isGenerateStepComplete(status: ProductGenerateStepStatus): boolean {
    return status === 'done' || status === 'synced';
}

/** Next required step is locked until every earlier required step is complete. */
export function canRunGenerateStep(
    steps: ProductGenerateStep[],
    stepId: ProductGenerateStepId,
): boolean {
    const required = steps.filter((step) => step.status !== 'skipped');
    const index = required.findIndex((step) => step.id === stepId);
    if (index < 0) return false;
    return required.slice(0, index).every((step) => isGenerateStepComplete(step.status));
}

function patchStep(
    steps: ProductGenerateStep[],
    id: ProductGenerateStepId,
    patch: Partial<ProductGenerateStep>,
): ProductGenerateStep[] {
    return steps.map((step) => (step.id === id ? { ...step, ...patch } : step));
}

function findQuoteIdForProduct(contractRes: unknown, product: PreContractProduct): string | null {
    const root = contractRes as Record<string, unknown> | null;
    const list = (root?.QuoteContract ?? root?.quoteContract) as unknown;
    if (!Array.isArray(list)) return null;

    const productMatchers: Record<PreContractProduct, RegExp> = {
        Solar: /solar/i,
        Roof: /roof/i,
        Battery: /battery/i,
        RRR: /rrr/i,
        RR: /\brr\b/i,
    };
    const matcher = productMatchers[product];

    const matches = list
        .map((row) => row as Record<string, unknown>)
        .filter((row) => {
            const label = String(
                row.Product_Type_Name ??
                    row.product_Type_Name ??
                    row.ProductCategory ??
                    row.productCategory ??
                    '',
            );
            return matcher.test(label);
        });

    const latest = matches[matches.length - 1];
    if (!latest) return null;

    const quoteId = String(
        latest.Finance_Partner_Quote_Id ??
            latest.finance_Partner_Quote_Id ??
            latest.QuoteId ??
            latest.quoteId ??
            '',
    ).trim();

    return quoteId || null;
}

export function formatLinkedQuoteDetail(quoteId: string): string {
    return `Quote #${quoteId}`;
}

const PLAN_GATED_STEP_IDS: ProductGenerateStepId[] = [
    'finalize',
    'utilityDetails',
    'partnerSync',
];

/**
 * Keep quote/contract run history, but refresh finalize / utility / partner from
 * current dirty flags. Closing a popup should not freeze those gates.
 */
export function reconcileGenerateStepsWithPlan(
    live: ProductGenerateStep[],
    planned: ProductGenerateStep[],
    running?: boolean,
): ProductGenerateStep[] {
    const liveById = new Map(live.map((step) => [step.id, step]));
    return planned.map((plannedStep) => {
        const current = liveById.get(plannedStep.id);
        if (!current) return plannedStep;
        if (running && current.status === 'running') return current;
        if (PLAN_GATED_STEP_IDS.includes(plannedStep.id)) {
            return {
                ...current,
                status: plannedStep.status,
                detail: plannedStep.detail,
            };
        }
        return current;
    });
}

export function applyFinancePartnerSyncToGenerateSteps(
    steps: ProductGenerateStep[],
    product: PreContractProduct,
    syncList: FinancePartnerSyncStatusRow[] | null | undefined,
): ProductGenerateStep[] {
    if (!Array.isArray(syncList) || syncList.length === 0) return steps;
    const productType = financePartnerProductTypeLabel(product);
    return steps.map((step) => {
        if (step.status === 'running' || step.status === 'error') return step;
        if (step.id === 'lead' && isFinancePartnerSyncFlagDone(syncList, productType, 'lead')) {
            return { ...step, status: 'synced', detail: 'Lead and contact already synced' };
        }
        if (step.id === 'utility' && isFinancePartnerSyncFlagDone(syncList, productType, 'utility')) {
            return { ...step, status: 'synced', detail: 'Already synced' };
        }
        if (
            step.id === 'leadSystem' &&
            isFinancePartnerSyncFlagDone(syncList, productType, 'leadSystem')
        ) {
            return { ...step, status: 'synced', detail: 'Already synced' };
        }
        return step;
    });
}

export function applyLinkedQuoteToGenerateSteps(
    steps: ProductGenerateStep[],
    quoteId?: string | null,
): ProductGenerateStep[] {
    const id = String(quoteId ?? '').trim();
    return steps.map((step) => {
        if (step.id !== 'quote') return step;
        if (step.status === 'running') return step;
        if (!id) {
            return {
                ...step,
                status: 'pending',
                quoteId: undefined,
                detail: undefined,
            };
        }
        return {
            ...step,
            status: 'done',
            quoteId: id,
            detail: formatLinkedQuoteDetail(id),
        };
    });
}

function mapGetQuotesProductType(value: unknown): PreContractProduct | null {
    const label = String(value ?? '').trim().toLowerCase();
    if (!label) return null;
    if (label === 'solar' || label === 's') return 'Solar';
    if (label === 'roof' || label === 'roof only' || label === 'r') return 'Roof';
    if (label === 'battery' || label === 'battery only' || label === 'b') return 'Battery';
    if (label.includes('solar')) return 'Solar';
    if (label.includes('roof')) return 'Roof';
    if (label.includes('battery')) return 'Battery';
    return null;
}

function readGetQuotesPartner(row: Record<string, unknown>): string {
    return String(
        row.FinancePartner ??
            row.financePartner ??
            row.Finance_Partner ??
            row.finance_Partner ??
            row.Finance_Partner_Display_Name ??
            row.finance_Partner_Display_Name ??
            '',
    ).trim();
}

export type ProductPartnerFilter = Partial<
    Record<PreContractProduct, { partner: string | null; method?: string | null }>
>;

/** Latest GetQuotes id per product that exists on the opportunity and matches its saved partner. */
export function latestQuoteIdsFromGetQuotes(
    raw: unknown,
    productPartners?: ProductPartnerFilter,
): Partial<Record<PreContractProduct, string>> {
    const list = Array.isArray((raw as { data?: unknown })?.data)
        ? (raw as { data: unknown[] }).data
        : Array.isArray(raw)
          ? raw
          : [];
    const out: Partial<Record<PreContractProduct, string>> = {};
    list.forEach((row) => {
        if (!row || typeof row !== 'object') return;
        const rec = row as Record<string, unknown>;
        const quoteId = String(rec.QuoteId ?? rec.quoteId ?? '').trim();
        const product = mapGetQuotesProductType(rec.ProductType ?? rec.productType);
        if (!quoteId || !product) return;
        if (productPartners) {
            const saved = productPartners[product];
            const savedPartner = String(saved?.partner ?? '').trim();
            if (!saved || !savedPartner) return;
            const quotePartner = readGetQuotesPartner(rec);
            if (!quotePartner || !partnerKeysEqual(quotePartner, savedPartner)) return;
        }
        out[product] = quoteId;
    });
    return out;
}

export async function runPreContractProductGeneratePipeline(
    args: RunProductGeneratePipelineArgs,
): Promise<ProductGenerateRunResult> {
    const {
        product,
        opportunityId,
        partnerName,
        leadAlreadyConfirmed = false,
        onStepUpdate,
        waitForQuoteSaved,
    } = args;
    const productType = financePartnerProductTypeLabel(product);
    let steps = buildInitialGenerateSteps(product, partnerName);

    const emit = (id: ProductGenerateStepId, patch: Partial<ProductGenerateStep>) => {
        steps = patchStep(steps, id, patch);
        const step = steps.find((entry) => entry.id === id);
        if (step) onStepUpdate(step);
    };

    for (const step of steps) {
        onStepUpdate(step);
    }

    if (partnerSkipsQuotePreSync(partnerName)) {
        // Cash / GoodLeap / Enfin: no lead, utility, or lead-system sync before the quote.
    } else {
        let syncList: FinancePartnerSyncStatusRow[] = [];
        try {
            syncList = await loadFinancePartnerSyncStatuses(opportunityId);
        } catch {
            syncList = [];
        }

        const leadStep = steps.find((step) => step.id === 'lead');
        if (leadStep?.status !== 'skipped') {
            const leadReady =
                leadAlreadyConfirmed ||
                isFinancePartnerSyncFlagDone(syncList, productType, 'lead');
            const contactReady = isFinancePartnerSyncFlagDone(
                syncList,
                productType,
                'contact',
            );
            if (leadReady && contactReady) {
                emit('lead', { status: 'synced', detail: 'Lead and contact already synced' });
            } else if (leadReady) {
                emit('lead', { status: 'running', detail: 'Syncing contact…' });
                try {
                    await ensureLeadAndContactForProduct({
                        opportunityId,
                        productType,
                        productCode: financePartnerProductCode(product),
                        createLeadIfMissing: false,
                        contactSyncExisting: true,
                    });
                    emit('lead', { status: 'done', detail: 'Lead and contact ready' });
                } catch (error) {
                    const message = getContractApiErrorMessage(error, 'Contact sync failed');
                    emit('lead', { status: 'error', detail: message });
                    return { ok: false, error: message };
                }
            } else {
                emit('lead', { status: 'running' });
                try {
                    await ensureLeadAndContactForProduct({
                        opportunityId,
                        productType,
                        productCode: financePartnerProductCode(product),
                        contactSyncExisting: false,
                    });
                    emit('lead', { status: 'done', detail: 'Lead ready' });
                } catch (error) {
                    const message = getContractApiErrorMessage(error, 'Lead sync failed');
                    emit('lead', { status: 'error', detail: message });
                    return { ok: false, error: message };
                }
            }
        }

        if (partnerNeedsUtilityAndLeadSystem(partnerName)) {
            const utilityStep = steps.find((step) => step.id === 'utility');
            if (utilityStep?.status !== 'skipped') {
                if (isFinancePartnerSyncFlagDone(syncList, productType, 'utility')) {
                    emit('utility', { status: 'synced', detail: 'Already synced' });
                } else {
                    emit('utility', { status: 'running' });
                    try {
                        await syncUtilityWithRetries({
                            opportunityId,
                            productType,
                            partnerName,
                        });
                        emit('utility', { status: 'done', detail: 'Utility synced' });
                    } catch (error) {
                        const message =
                            error instanceof Error ? error.message : 'Utility sync failed.';
                        emit('utility', { status: 'error', detail: message });
                        return { ok: false, error: message };
                    }
                }
            }

            const leadSystemStep = steps.find((step) => step.id === 'leadSystem');
            if (leadSystemStep?.status !== 'skipped') {
                if (isFinancePartnerSyncFlagDone(syncList, productType, 'leadSystem')) {
                    emit('leadSystem', { status: 'synced', detail: 'Already synced' });
                } else {
                    emit('leadSystem', { status: 'running' });
                    try {
                        await syncLeadSystemWithRetries({
                            opportunityId,
                            productType,
                            productCategoryOverride:
                                useOneButtonStore.getState().productType ||
                                financePartnerProductCode(product),
                        });
                        emit('leadSystem', { status: 'done', detail: 'Lead system synced' });
                    } catch (error) {
                        const message =
                            error instanceof Error ? error.message : 'Lead system sync failed.';
                        emit('leadSystem', { status: 'error', detail: message });
                        return { ok: false, error: message };
                    }
                }
            }
        }
    }

    emit('quote', { status: 'running', detail: 'Opening quote form…' });
    try {
        const savedQuoteId = await waitForQuoteSaved();
        const quoteId = String(savedQuoteId ?? '').trim();
        emit('quote', {
            status: 'done',
            quoteId: quoteId || undefined,
            detail: quoteId ? formatLinkedQuoteDetail(quoteId) : 'Quote saved',
        });
    } catch (error) {
        if (isGenerateStepDismissed(error)) {
            return { ok: true, dismissed: true };
        }
        const message =
            error instanceof Error ? error.message : 'Quote was not completed.';
        emit('quote', { status: 'error', detail: message });
        return { ok: false, error: message };
    }

    emit('contract', { status: 'running' });
    try {
        const contractRes = await quoteServices.retrieveContract(opportunityId);
        const quoteId = findQuoteIdForProduct(contractRes, product);
        if (!quoteId) {
            throw new Error('No saved quote found for this product.');
        }

        const contractResPayload = await quoteServices.createContractFinancePartner({
            quoteId,
            opportunityId,
            action: 'embed',
            salesRep: false,
            loggedIn: false,
            customer: false,
        });

        const file =
            (typeof (contractResPayload as Record<string, unknown>)?.file === 'string' &&
                (contractResPayload as Record<string, string>).file) ||
            (typeof (contractResPayload as Record<string, unknown>)?.File === 'string' &&
                (contractResPayload as Record<string, string>).File) ||
            null;

        if (!file) {
            const message =
                getContractApiResultErrorMessage(contractResPayload, '') ||
                'Contract API did not return a document.';
            throw new Error(message);
        }

        emit('contract', { status: 'done', detail: 'Contract generated' });
        return { ok: true };
    } catch (error) {
        const message = getContractApiErrorMessage(error, 'Contract generation failed');
        emit('contract', { status: 'error', detail: message });
        return { ok: false, error: message };
    }
}

export type RunProductGenerateStepArgs = RunProductGeneratePipelineArgs & {
    stepId: ProductGenerateStepId;
};

/** Run one required quote-pipeline step. Unused partner steps are omitted from the list. */
export async function runPreContractProductGenerateStep(
    args: RunProductGenerateStepArgs,
): Promise<ProductGenerateRunResult> {
    const { stepId, product, opportunityId, partnerName, onStepUpdate, waitForQuoteSaved } = args;
    const productType = financePartnerProductTypeLabel(product);

    const emit = (patch: Partial<ProductGenerateStep>) => {
        onStepUpdate({
            id: stepId,
            label:
                stepId === 'lead'
                    ? 'Create lead'
                    : stepId === 'utility'
                      ? 'Sync utility'
                      : stepId === 'leadSystem'
                        ? 'Sync lead system'
                        : stepId === 'quote'
                          ? 'Create quote'
                          : stepId === 'contract'
                            ? 'Generate contract'
                            : 'Sync partner',
            status: 'pending',
            ...patch,
        });
    };

    if (stepId === 'quote') {
        emit({ status: 'running', detail: 'Opening quote form…' });
        try {
            const savedQuoteId = await waitForQuoteSaved();
            const quoteId = String(savedQuoteId ?? '').trim();
            emit({
                status: 'done',
                quoteId: quoteId || undefined,
                detail: quoteId ? formatLinkedQuoteDetail(quoteId) : 'Quote saved',
            });
            return { ok: true };
        } catch (error) {
            if (isGenerateStepDismissed(error)) {
                return { ok: true, dismissed: true };
            }
            const message = error instanceof Error ? error.message : 'Quote was not completed.';
            emit({ status: 'error', detail: message });
            return { ok: false, error: message };
        }
    }

    if (stepId === 'contract') {
        emit({ status: 'running' });
        try {
            const contractRes = await quoteServices.retrieveContract(opportunityId);
            const quoteId = findQuoteIdForProduct(contractRes, product);
            if (!quoteId) {
                throw new Error('No saved quote found for this product. Create the quote first.');
            }
            const contractResPayload = await quoteServices.createContractFinancePartner({
                quoteId,
                opportunityId,
                action: 'embed',
                salesRep: false,
                loggedIn: false,
                customer: false,
            });
            const file =
                (typeof (contractResPayload as Record<string, unknown>)?.file === 'string' &&
                    (contractResPayload as Record<string, string>).file) ||
                (typeof (contractResPayload as Record<string, unknown>)?.File === 'string' &&
                    (contractResPayload as Record<string, string>).File) ||
                null;
            if (!file) {
                const message =
                    getContractApiResultErrorMessage(contractResPayload, '') ||
                    'Contract API did not return a document.';
                throw new Error(message);
            }
            emit({ status: 'done', detail: 'Contract generated' });
            return { ok: true };
        } catch (error) {
            const message = getContractApiErrorMessage(error, 'Contract generation failed');
            emit({ status: 'error', detail: message });
            return { ok: false, error: message };
        }
    }

    if (partnerSkipsQuotePreSync(partnerName) && stepId !== 'partnerSync') {
        emit({ status: 'skipped', detail: `Not required for ${partnerName.trim()}` });
        return { ok: true };
    }

    let syncList: FinancePartnerSyncStatusRow[] = [];
    try {
        syncList = await loadFinancePartnerSyncStatuses(opportunityId);
    } catch {
        syncList = [];
    }

    if (stepId === 'lead') {
        const leadReady =
            args.leadAlreadyConfirmed ||
            isFinancePartnerSyncFlagDone(syncList, productType, 'lead');
        const contactReady = isFinancePartnerSyncFlagDone(syncList, productType, 'contact');
        if (leadReady && contactReady) {
            emit({ status: 'synced', detail: 'Lead and contact already synced' });
            return { ok: true };
        }
        emit({ status: 'running', detail: leadReady ? 'Syncing contact…' : undefined });
        try {
            await ensureLeadAndContactForProduct({
                opportunityId,
                productType,
                productCode: financePartnerProductCode(product),
                createLeadIfMissing: !leadReady,
                contactSyncExisting: leadReady,
            });
            emit({
                status: 'done',
                detail: leadReady ? 'Lead and contact ready' : 'Lead ready',
            });
            return { ok: true };
        } catch (error) {
            const message = getContractApiErrorMessage(error, 'Lead sync failed');
            emit({ status: 'error', detail: message });
            return { ok: false, error: message };
        }
    }

    if (stepId === 'utility') {
        if (isFinancePartnerSyncFlagDone(syncList, productType, 'utility')) {
            emit({ status: 'synced', detail: 'Already synced' });
            return { ok: true };
        }
        emit({ status: 'running' });
        try {
            await syncUtilityWithRetries({ opportunityId, productType, partnerName });
            emit({ status: 'done', detail: 'Utility synced' });
            return { ok: true };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Utility sync failed.';
            emit({ status: 'error', detail: message });
            return { ok: false, error: message };
        }
    }

    if (stepId === 'leadSystem') {
        if (isFinancePartnerSyncFlagDone(syncList, productType, 'leadSystem')) {
            emit({ status: 'synced', detail: 'Already synced' });
            return { ok: true };
        }
        emit({ status: 'running' });
        try {
            await syncLeadSystemWithRetries({
                opportunityId,
                productType,
                productCategoryOverride: financePartnerProductCode(product),
            });
            emit({ status: 'done', detail: 'Lead system synced' });
            return { ok: true };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Lead system sync failed.';
            emit({ status: 'error', detail: message });
            return { ok: false, error: message };
        }
    }

    return { ok: false, error: 'This step is not available.' };
}
